import { groupByQuarter } from '../engines/dateUtils';

/**
 * Build the QUARTERLY column descriptors the statement pages render.
 *
 * WHY THIS EXISTS. groupByQuarter() returns an OBJECT keyed "YYYY-QN", which is
 * what its JSDoc documents and what KPIDashboardPage has always consumed
 * correctly. Three other pages called .map() on that object directly and threw
 * "groupByQuarter(...).map is not a function", blanking the page. The function
 * was never at fault; the same adapter was simply written four times and three
 * of them were wrong. This is the one spelling of that adapter.
 *
 * NOTE the mismatch was two-deep: the broken callers destructured
 * { year, quarter, months }, but the object's values are bare month arrays.
 * Object.values() alone would have produced undefined labels and undefined
 * months - working code that renders blanks. The year and the quarter are
 * recovered HERE, from the key.
 *
 * DO NOT change groupByQuarter's return contract to "fix" this. Its object
 * shape is documented and depended upon; changing it would break the only
 * caller that was already correct.
 *
 * @param {string[]} months  the tenant's own window, e.g. ["2026-08", ...]
 * @param {Object}  [opts]
 * @param {"short"|"numYear"|"shortYear"} [opts.label="short"]
 *        "short"     -> "Q3"       (Balance Sheet, Income Statement, Cash Flow -- the year is in their band row)
 *        "numYear"   -> "Q3 26"    (no page uses it since W-045, 2026-10-02)
 *        "shortYear" -> "Q3 '26"   (KPI Dashboard; matches formatMonthLabel's
 *                                   "shortYear" idiom, which uses an apostrophe)
 * @returns {{key: string, label: string, months: string[], year: number}[]}
 *          in chronological order. The four fields are exactly what the pages
 *          consume; nothing extra is emitted, so the descriptors stay
 *          interchangeable with what each page built before.
 *
 * @example quarterColumns(["2026-08","2026-09","2026-10","2026-11","2026-12"])
 *   -> [ { key: "2026-Q3", label: "Q3 (partial)", months: [...2], year: 2026 },
 *        { key: "2026-Q4", label: "Q4",           months: [...3], year: 2026 } ]
 */
export function quarterColumns(months, opts = {}) {
  const labelMode = opts.label || 'short';
  const byQ = groupByQuarter(months || []);

  // Keys are "YYYY-QN" with a 4-digit year and a single-digit quarter, so a
  // plain lexicographic sort is chronological. This mirrors what KPI already
  // did; the three broken callers relied on object iteration order instead.
  return Object.keys(byQ).sort().map((qKey) => {
    const ms = byQ[qKey];
    const [yStr, qStr] = qKey.split('-Q');
    const year = parseInt(yStr, 10);

    // PARTIAL IS RELATIVE TO THE CALENDAR QUARTER, NOT TO THE DATA. A calendar
    // quarter has three months; this bucket holds only the months the window
    // actually covers, so fewer than three means the window starts or ends
    // mid-quarter. A month whose values are zero is still a covered month and
    // is NOT partial. Handles either end of the window with one test.
    const isPartial = ms.length < 3;

    let label;
    if (labelMode === 'numYear') label = 'Q' + qStr + ' ' + yStr.slice(2);
    else if (labelMode === 'shortYear') label = 'Q' + qStr + " '" + yStr.slice(2);
    else label = 'Q' + qStr;

    // Header cells are whiteSpace:nowrap over a minWidth (a floor, not a
    // ceiling), so the longer label widens the column rather than truncating.
    if (isPartial) label += ' (partial)';

    return { key: qKey, label, months: ms, year };
  });
}
